import { Link, useRoute, useLocation } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import { ArrowLeft, CheckCircle, XCircle, Calendar, User, Clock, Users, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { staffService } from '@/services/api/staff.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/hooks/useCurrency';
import type { BookingStatus } from '@/lib/status-colors';
import { SessionNoteCard } from '@/components/sessions/SessionNoteCard';
import { transformBookingNoteSummary } from '@/services/api/sessions.service';

export default function StaffBookingDetailsPage() {
  const { t } = useTranslation(['bookings', 'common', 'staff']);
  const [, params] = useRoute('/staff/bookings/:id');
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();

  const bookingId = (params as { id?: string })?.id || null;

  const statusLabel = (status: string) =>
    t(`staff:status.${status === 'no_show' ? 'noShow' : status}`, status.replace('_', ' '));

  const { data: booking, isLoading, error } = useQuery({
    queryKey: ['staff-booking', bookingId],
    queryFn: () => staffService.getBooking(bookingId!),
    enabled: !!bookingId,
  });

  const confirmMutation = useMutation({
    mutationFn: (id: number) => staffService.confirmBooking(id),
    onSuccess: () => {
      toast({ title: t('common:toasts.success'), description: t('bookings:toasts.bookingConfirmed') });
      queryClient.invalidateQueries({ queryKey: ['staff-booking', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['staff-schedule'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common:errors.generic'),
        description: error.message || t('bookings:toasts.confirmError'),
        variant: 'destructive',
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => staffService.cancelBooking(id, t('bookings:details.cancelledByStaff')),
    onSuccess: () => {
      toast({ title: t('common:toasts.success'), description: t('bookings:toasts.bookingCancelled') });
      queryClient.invalidateQueries({ queryKey: ['staff-booking', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['staff-schedule'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common:errors.generic'),
        description: error.message || t('bookings:toasts.cancelError'),
        variant: 'destructive',
      });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: number) => staffService.completeBooking(id),
    onSuccess: () => {
      toast({ title: t('common:toasts.success'), description: t('bookings:toasts.markedCompleted') });
      queryClient.invalidateQueries({ queryKey: ['staff-booking', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['staff-schedule'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common:errors.generic'),
        description: error.message || t('bookings:toasts.completeError'),
        variant: 'destructive',
      });
    },
  });

  const noShowMutation = useMutation({
    mutationFn: (id: number) => staffService.markNoShow(id),
    onSuccess: () => {
      toast({ title: t('common:toasts.success'), description: t('bookings:toasts.markedNoShow') });
      queryClient.invalidateQueries({ queryKey: ['staff-booking', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['staff-schedule'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common:errors.generic'),
        description: error.message || t('bookings:toasts.noShowError'),
        variant: 'destructive',
      });
    },
  });

  const goBack = () => {
    setLocation('/staff/schedule');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-soft">{t('bookings:details.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={goBack}
            iconLeft={<ArrowLeft />}
          >
            {t('common:back')}
          </Button>
        </div>
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-danger mb-4">{t('bookings:details.notFound')}</p>
            <Button onClick={goBack}>
              {t('bookings:details.backToSchedule')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-h1 text-ink">
              {t('bookings:details.title')}
            </h1>
            <p className="text-body-sm text-soft font-mono">
              {booking.booking_code}
            </p>
          </div>
          <StatusBadge status={booking.status as BookingStatus} className="shrink-0">
            {statusLabel(booking.status)}
          </StatusBadge>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={goBack}
          iconLeft={<ArrowLeft />}
        >
          {t('common:back')}
        </Button>
      </div>

      {/* Group-class banner — links to the full class with all participants */}
      {(() => {
        const slot = (booking as any).available_slot;
        const cap = slot?.max_capacity ?? (booking as any).service?.max_capacity ?? 1;
        if (cap > 1 && slot?.id) {
          return (
            <button
              onClick={() => setLocation(`/staff/group-classes/${slot.id}`)}
              className="w-full flex items-center justify-between gap-3 rounded-lg border border-line bg-surface-2 px-4 py-3 hover:bg-surface-2/70 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Users className="h-5 w-5 text-brand-500 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-label text-ink truncate">
                    {t('staff:availability.groupClass.review.title', 'Group class')} · {slot.current_bookings ?? 0}/{cap}
                  </div>
                  <div className="text-body-xs text-soft">
                    {t('staff:availability.groupClass.openFullPage', 'Open full page')}
                  </div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-soft flex-shrink-0" />
            </button>
          );
        }
        return null;
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SessionNoteCard bookingId={booking.public_id ?? booking.id} summary={transformBookingNoteSummary(booking.session_note)} />
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {t('bookings:details.customerInfo')}
                </span>
                {booking.customer_id != null && (
                  <Link
                    href={`/admin/customers/${booking.customer_id}`}
                    className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline"
                  >
                    {t('bookings:details.viewCustomer', 'View customer')}
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.name')}</p>
                  <p className="text-base">{booking.customer_full_name}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.email')}</p>
                  <p className="text-base break-all">{booking.customer_email}</p>
                </div>
                {booking.customer_phone && (
                  <div>
                    <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.phone')}</p>
                    <p className="text-base">{booking.customer_phone}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t('bookings:details.appointmentInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.service')}</p>
                  <p className="text-base">{booking.service?.name || booking.service_name}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.provider')}</p>
                  <p className="text-base">{booking.provider?.name || booking.provider?.display_name || '—'}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.date')}</p>
                  <p className="text-base">
                    {booking.start_time && format(parseISO(booking.start_time), getDateFormats().fullWithWeekday, { locale: getDateLocale() })}
                  </p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.time')}</p>
                  <p className="text-base">
                    {booking.start_time && booking.end_time &&
                      `${format(parseISO(booking.start_time), 'HH:mm')} - ${format(parseISO(booking.end_time), 'HH:mm')}`
                    }
                  </p>
                </div>
                {booking.service?.duration_minutes && (
                  <div>
                    <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.duration')}</p>
                    <p className="text-base">{t('bookings:details.minutesValue', { count: booking.service.duration_minutes })}</p>
                  </div>
                )}
                {booking.service?.price && (
                  <div>
                    <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.price')}</p>
                    <p className="text-base">{formatCurrency(booking.service.price)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {booking.notes && (
            <Card>
              <CardHeader>
                <CardTitle>{t('bookings:details.notes')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-body text-ink bg-surface-2 p-3 rounded-lg">
                  {booking.notes}
                </p>
              </CardContent>
            </Card>
          )}

          {booking.custom_answers && Object.keys(booking.custom_answers).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('common:labels.customQuestions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-surface-2 border border-line rounded-lg p-4 space-y-3">
                  {Object.entries(booking.custom_answers).map(([questionId, answer]) => {
                    const service = booking.service || (booking as any).available_slot?.service;
                    const customQuestions = service?.custom_booking_questions || service?.customBookingQuestions || [];
                    const question = customQuestions.find((q: any) => q.id === questionId);

                    if (!question) {
                      const displayAnswer = Array.isArray(answer) ? answer.join(', ') : String(answer);
                      return (
                        <div key={questionId} className="border-b border-line last:border-0 pb-2 last:pb-0">
                          <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fallbackQuestion')}</p>
                          <p className="text-body text-ink">{displayAnswer}</p>
                        </div>
                      );
                    }

                    const questionText = question.question;
                    const displayAnswer = Array.isArray(answer) ? answer.join(', ') : String(answer);
                    return (
                      <div key={questionId} className="border-b border-line last:border-0 pb-2 last:pb-0">
                        <p className="text-mono-eyebrow uppercase text-soft">{questionText}</p>
                        <p className="text-body text-ink">{displayAnswer}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('bookings:details.bookingDetails')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.bookingId')}</p>
                  <p className="text-base font-mono">{booking.booking_code}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.status')}</p>
                  <StatusBadge status={booking.status as BookingStatus}>
                    {statusLabel(booking.status)}
                  </StatusBadge>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.created')}</p>
                  <p className="text-base">
                    {format(parseISO(booking.created_at), `${getDateFormats().medium} HH:mm`, { locale: getDateLocale() })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('bookings:details.actionsTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {booking.status === 'pending' && (
                <Button
                  variant="success"
                  onClick={() => confirmMutation.mutate(booking.id)}
                  className="w-full"
                  disabled={confirmMutation.isPending}
                  iconLeft={<CheckCircle />}
                >
                  {t('bookings:actions.confirmBooking')}
                </Button>
              )}
              {booking.status === 'confirmed' && (
                <>
                  <Button
                    variant="primary"
                    onClick={() => completeMutation.mutate(booking.id)}
                    className="w-full"
                    disabled={completeMutation.isPending}
                    iconLeft={<CheckCircle />}
                  >
                    {t('bookings:actions.markCompleted')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => noShowMutation.mutate(booking.id)}
                    className="w-full"
                    disabled={noShowMutation.isPending}
                    iconLeft={<Clock />}
                  >
                    {t('bookings:actions.markNoShow')}
                  </Button>
                </>
              )}
              {['pending', 'confirmed'].includes(booking.status) && (
                <Button
                  variant="destructive"
                  onClick={() => cancelMutation.mutate(booking.id)}
                  className="w-full"
                  disabled={cancelMutation.isPending}
                  iconLeft={<XCircle />}
                >
                  {t('bookings:actions.cancelBooking')}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
